// External Imports
import moment from "moment";
import { Col, Row } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";

// Internal Imports
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import CardNew from "components/molecules/Card/CardNew";
import Button from "components/atoms/Button/Button";
import Star from "pages/common/ShipmentBilling/components/Star/Star";
import RateNowDialog from "pages/common/ShipmentBilling/components/RateNowDialog/RateNowDialog";
import { getProviderPerformanceReview } from "api";
import { renderAddressV2InfoForHeaders } from "helpers/addressV2Helpers";
import { getFreightModeIcon } from "helpers/freightModeHelper";
import CopyToClipboard from "components/molecules/CopyToClipboard/CopyToClipboard";
import usePermissions from "hooks/usePermissions";
import Cookies from "js-cookie";
import ConsolidatedModal from "../../../ShipmentQuotes/components/ConsolidatedModal";

/**
 * A label component for each tracking info column
 * @param {*} param0 - the label text or component
 * @returns {Object} - a label component
 */
function Label({ children }) {
  return (
    <div className="mb-2">
      <p className="font-b5 text-secondary">{children}</p>
    </div>
  );
}

/**
 * Formats the expected and updated pickup and delivery dates
 * @param {*} stop - the pickup or delivery stop
 * @returns {Object} - an object containing the expected and updated pickup and delivery dates
 */
function formatStopDateTime(stop) {
  const { expected_stop_date, expected_stop_time, updated_stop_date, updated_stop_time } = stop;

  const formatDate = (date) => moment(date).format("MMM DD ");
  const formatTime = (time) => moment(time, "HHmmss").format("HH:mm");

  const expectedDate = formatDate(expected_stop_date);
  const expectedTime = formatTime(expected_stop_time);
  const expectedDateTimeString = `${expectedDate} - ${expectedTime}`;

  const updatedDate = formatDate(updated_stop_date);
  const updatedTime = formatTime(updated_stop_time);
  const updatedDateTimeString = `${updatedDate} - ${updatedTime}`;

  return {
    expectedDateTimeString,
    updatedDateTimeString,
  };
}

/**
 * Gets the expected and updated pickup and delivery dates
 * @param {*} shipmentDetails - shipment details from the API
 * @returns {Object} - an object containing the expected and updated pickup and delivery dates
 */
function getPickupAndDeliveryDates(shipmentDetails) {
  const {
    actual_pickup_date,
    actual_pickup_time,
    quoted_pickup_date,
    quoted_pickup_time,
    stop1_actual_delivery_date,
    stop1_actual_delivery_time,
    stop1_quoted_delivery_date,
    stop1_quoted_delivery_time,
    schedule_stops,
  } = shipmentDetails.schedule;

  const pickupScheduledStop = schedule_stops.find((stop) => stop.type === "pickup") || {};
  const deliveryScheduledStop = schedule_stops.find((stop) => stop.type === "delivery") || {};

  const pickupStop = {
    updated_stop_date: pickupScheduledStop?.updated_stop_date || actual_pickup_date,
    updated_stop_time: pickupScheduledStop?.updated_stop_time || actual_pickup_time,
    expected_stop_date: pickupScheduledStop?.expected_stop_date || quoted_pickup_date,
    expected_stop_time: pickupScheduledStop?.expected_stop_time || quoted_pickup_time,
  };
  const deliveryStop = {
    updated_stop_date: deliveryScheduledStop?.updated_stop_date || stop1_actual_delivery_date,
    updated_stop_time: deliveryScheduledStop?.updated_stop_time || stop1_actual_delivery_time,
    expected_stop_date: deliveryScheduledStop?.expected_stop_date || stop1_quoted_delivery_date,
    expected_stop_time: deliveryScheduledStop?.expected_stop_time || stop1_quoted_delivery_time,
  };

  const { expectedDateTimeString: expectedPickupDateTime, updatedDateTimeString: updatedPickupDateTime } =
    formatStopDateTime(pickupStop);
  const { expectedDateTimeString: expectedDeliveryDateTime, updatedDateTimeString: updatedDeliveryDateTime } =
    formatStopDateTime(deliveryStop);

  return {
    expectedPickupDateTime,
    updatedPickupDateTime,
    expectedDeliveryDateTime,
    updatedDeliveryDateTime,
  };
}

function TrackingInfo({ isLoading, shipment, shipmentDetails, isOffPlatform }) {
  // If loading, show skeleton
  if (isLoading)
    return (
      <CardNew className="w-100 border-radius-top-0">
        <LoadingSkeleton rows={1} noShadow isListing />
      </CardNew>
    );

  // State
  const [showRateDialog, setShowRateDialog] = useState(false);
  const [showConsolidatedModal, setShowConsolidatedModal] = useState(false);
  const [isLoadingPerformanceRating, setIsLoadingPerformanceRating] = useState(false);
  const [providerPerformanceRating, setProviderPerformanceRating] = useState(null);

  const { permissions } = usePermissions();
  const { user_type } = JSON.parse(permissions || "{}");
  const isShipper = user_type === "shipper";
  const hasShipmentDetailsData = !!Object.keys(shipmentDetails || {})?.length;
  const { quick_request_details } = shipmentDetails || {};
  // Extract data from shipment or shipment details based on which is available
  const extractData = () => ({
    shipment_id: hasShipmentDetailsData ? shipmentDetails.organization.shipment_id : shipment.shipment_reference,
    shipper_company: hasShipmentDetailsData ? shipmentDetails.organization.shipper_company : shipment.shipper,
    provider_company: hasShipmentDetailsData ? shipmentDetails.organization.provider_company : shipment.awarded_provider,
    pickup_address_name: hasShipmentDetailsData ? shipmentDetails.schedule.pickup_address_name : shipment.origin.origin_name,
    pickup_city: hasShipmentDetailsData ? shipmentDetails.schedule.pickup_city : shipment.origin.origin_city,
    pickup_country: hasShipmentDetailsData ? shipmentDetails.schedule.pickup_country : shipment.origin.origin_country,
    pickup_postal_code: hasShipmentDetailsData ? shipmentDetails.schedule.pickup_postal_code : shipment.pickup_zipcode,
    stop1_address_name: hasShipmentDetailsData
      ? shipmentDetails.schedule.stop1_address_name
      : shipment.delivery.destination_name,
    stop1_city: hasShipmentDetailsData ? shipmentDetails.schedule.stop1_city : shipment.delivery.destination_city,
    stop1_country: hasShipmentDetailsData ? shipmentDetails.schedule.stop1_country : shipment.delivery.destination_country,
    stop1_postal_code: hasShipmentDetailsData ? shipmentDetails.schedule.stop1_postal_code : shipment.stop1_zipcode,
    freight_mode: hasShipmentDetailsData ? shipmentDetails.service.freight_mode : shipment.freight_mode,
    shipment_status: hasShipmentDetailsData ? shipmentDetails.organization.shipment_status : shipment.display_status.label,
    expectedPickupDateTime: hasShipmentDetailsData
      ? getPickupAndDeliveryDates(shipmentDetails).expectedPickupDateTime
      : moment(shipment.origin.quoted_pickup_date).format("MMM DD - HH:mm"),
    updatedPickupDateTime: hasShipmentDetailsData
      ? getPickupAndDeliveryDates(shipmentDetails).updatedPickupDateTime
      : moment.utc(shipment.origin.updated_pickup_date).format("MMM DD - HH:mm"),
    expectedDeliveryDateTime: hasShipmentDetailsData
      ? getPickupAndDeliveryDates(shipmentDetails).expectedDeliveryDateTime
      : moment(shipment.delivery.quoted_delivery_date).format("MMM DD - HH:mm"),
    updatedDeliveryDateTime: hasShipmentDetailsData
      ? getPickupAndDeliveryDates(shipmentDetails).updatedDeliveryDateTime
      : moment.utc(shipment.delivery.updated_delivery_date).format("MMM DD - HH:mm"),
  });

  // Data declarations
  const {
    shipment_status,
    shipment_id,
    shipper_company,
    provider_company,
    pickup_address_name,
    pickup_city,
    pickup_country,
    pickup_postal_code,
    stop1_address_name,
    stop1_city,
    stop1_country,
    stop1_postal_code,
    freight_mode,
  } = extractData();

  const { address_v2 } = shipmentDetails?.schedule ?? {};
  const { renderPickupAddress, renderDeliveryAddress } = renderAddressV2InfoForHeaders({ data: shipmentDetails });

  const FreightModeIcon = getFreightModeIcon(freight_mode, "", true);

  // If shipment is delivered, get provider performance rating
  useEffect(() => {
    if (shipment_status === "Delivered" && !showRateDialog && Cookies.get("is_guest") !== "true") {
      setIsLoadingPerformanceRating(true);
      getProviderPerformanceReview(shipment_id)
        .then((res) => setProviderPerformanceRating(res.data.overall_performance_rating))
        .finally(() => setIsLoadingPerformanceRating(false));
    }
  }, [showRateDialog]);

  return (
    <>
      {/* Provider Performance Rating Dialog */}
      <RateNowDialog
        id={shipment_id}
        isOpen={showRateDialog}
        close={() => {
          setShowRateDialog(false);
        }}
      />
      {/* Quick request consolidation Dialog */}
      <ConsolidatedModal
        data={quick_request_details}
        isOpen={showConsolidatedModal}
        close={() => setShowConsolidatedModal(false)}
      />
      <CardNew className="w-100 border-radius-top-0">
        <CardNew.Body>
          <Row>
            {/* Provider */}
            <Col className="col-12 mb-3" md>
              <Label>
                {isShipper ? (
                  <FormattedMessage id="tracking.handledBy" defaultMessage="Handled by" />
                ) : (
                  <FormattedMessage id="tracking.requestedBy" defaultMessage="Requested by" />
                )}
              </Label>
              <div className="font-b2">
                <span className="mr-2">
                  <FreightModeIcon width={18} />
                </span>
                {isShipper ? provider_company : shipper_company}
                {/* Provider performance rating button */}
                {isShipper &&
                  shipment_status === "Delivered" &&
                  !isLoadingPerformanceRating &&
                  Cookies.get("is_guest") !== "true" &&
                  !shipmentDetails?.viewing_as_guest_user &&
                  (providerPerformanceRating ? (
                    <div className="d-flex">
                      <Link
                        to="#"
                        className="mt-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowRateDialog(true);
                        }}
                      >
                        <Star color="#FDB833" count={providerPerformanceRating} size="sm" />
                      </Link>
                    </div>
                  ) : (
                    <div className="d-flex">
                      <Button
                        variant="link"
                        className="mt-1 pl-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowRateDialog(true);
                        }}
                      >
                        <FormattedMessage id="billing.rate" defaultMessage="Rate" />
                      </Button>
                    </div>
                  ))}
              </div>
              {quick_request_details && quick_request_details.length > 1 && (
                <Button size="md" variant="link" className="font-bold pl-0" onClick={() => setShowConsolidatedModal(true)}>
                  <FormattedMessage id="general.viewConsolidatedOrders" defaultMessage="View consolidated orders" />
                </Button>
              )}
            </Col>
            {/* Pickup address */}
            <Col className="col-12 mb-3" md>
              <Label>
                <FormattedMessage id="general.pickup" defaultMessage="Pickup" />
              </Label>
              {Object.keys(address_v2 ?? {}).length > 0 ? (
                renderPickupAddress()
              ) : isOffPlatform ? ( // only show address name, city and country for off platform shipments
                <CopyToClipboard
                  text={[pickup_address_name, pickup_city, pickup_country].filter(Boolean).join(", ")}
                  className="mb-2"
                  copyOnly
                >
                  <div>
                    <p className="font-b2">{pickup_address_name}</p>
                    <p className="font-b2 mt-1">{[pickup_city, pickup_country].filter(Boolean).join(", ")}</p>
                  </div>
                </CopyToClipboard>
              ) : (
                // show full address
                <CopyToClipboard
                  text={[
                    pickup_address_name,
                    shipmentDetails.schedule?.pickup_addressline1,
                    shipmentDetails.schedule?.pickup_addressline2,
                    pickup_postal_code,
                    pickup_city,
                    pickup_country,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                  className="mb-2"
                  copyOnly
                >
                  <div>
                    <p className="font-b2">{pickup_address_name}</p>
                    <p className="font-b2 mt-1">
                      {shipmentDetails.schedule?.pickup_addressline1}
                      {shipmentDetails.schedule?.pickup_addressline2 && `, ${shipmentDetails.schedule?.pickup_addressline2}`}
                    </p>
                    <p className="font-b2 mt-1">
                      {[pickup_postal_code, pickup_city, pickup_country].filter(Boolean).join(", ")}
                    </p>
                  </div>
                </CopyToClipboard>
              )}
            </Col>
            {/* Delivery address */}
            <Col className="col-12 mb-3" md>
              <Label>
                <FormattedMessage id="general.delivery" defaultMessage="Delivery" />
              </Label>
              {Object.keys(address_v2 ?? {}).length > 0 ? (
                renderDeliveryAddress()
              ) : isOffPlatform ? ( // only show address name, city and country for off platform shipments
                <CopyToClipboard
                  text={[stop1_address_name, stop1_city, stop1_country].filter(Boolean).join(", ")}
                  className="mb-2"
                  copyOnly
                >
                  <div>
                    <p className="font-b2">{stop1_address_name}</p>
                    <p className="font-b2 mt-1">{[stop1_city, stop1_country].filter(Boolean).join(", ")}</p>
                  </div>
                </CopyToClipboard>
              ) : (
                // show full address
                <CopyToClipboard
                  text={[
                    stop1_address_name,
                    shipmentDetails.schedule?.stop1_addressline1,
                    shipmentDetails.schedule?.stop1_addressline2,
                    stop1_postal_code,
                    stop1_city,
                    stop1_country,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                  className="mb-2"
                  copyOnly
                >
                  <div>
                    <p className="font-b2">{stop1_address_name}</p>
                    <p className="font-b2 mt-1">
                      {shipmentDetails.schedule?.stop1_addressline1}
                      {shipmentDetails.schedule?.stop1_addressline2 && `, ${shipmentDetails.schedule?.stop1_addressline2}`}
                    </p>
                    <p className="font-b2 mt-1">
                      {[stop1_postal_code, stop1_city, stop1_country].filter(Boolean).join(", ")}
                    </p>
                  </div>
                </CopyToClipboard>
              )}
            </Col>
            {/* Delivery planning */}
          </Row>
        </CardNew.Body>
      </CardNew>
    </>
  );
}

Label.propTypes = {
  children: PropTypes.node.isRequired,
};

TrackingInfo.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  shipment: PropTypes.instanceOf(Object).isRequired,
  shipmentDetails: PropTypes.instanceOf(Object).isRequired,
  isOffPlatform: PropTypes.bool.isRequired,
};

export default TrackingInfo;
